import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { currency, displayDate, displayDateTime } from '@/utils/format.utils';
import { calcPlanValue, getBenefactor, getOrderStatusVariant, getOutstanding, getTotalPaid } from '@/utils/order.utils';
import type { ColumnDef } from '@tanstack/vue-table';
import { ArrowUpDown } from 'lucide-vue-next';
import { h } from 'vue';
import { RouterLink } from 'vue-router';
import type { ModelOrderResponse, ModelPaymentPlanResponse } from '@layup/api-sdk';

export const paymentPlansColumns: ColumnDef<ModelOrderResponse>[] = [
  {
    accessorKey: 'customerName',
    enableSorting: false,
    header: 'Customer Name',
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', getBenefactor(order)?.name ?? 'N/A');
    },
  },
  {
    accessorKey: 'name',
    header: 'Order Name',
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.name;
      const id = row.original._id;
      return h(RouterLink, { to: `/merchant/orders/${id}`, class: 'text-brand-700 underline p-0 m-0 ' }, () => name);
    },
  },
  {
    accessorKey: 'reference',
    header: 'Order Reference',
    enableSorting: false,
    cell: ({ getValue }) => getValue() as string,
  },
  {
    accessorKey: 'dateCreated',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Created Date', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    enableSorting: true,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', displayDate(order.createdAt));
    },
  },
  {
    accessorKey: 'expireDate',
    header: 'Expires At',
    enableSorting: false,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', displayDateTime(order.expire));
    },
  },
  {
    accessorKey: 'finalPayment',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Final Payment Date', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    enableSorting: true,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', displayDate(order.endDateMax));
    },
  },
  {
    accessorKey: 'dateUpdated',
    header: 'Status Updated',
    enableSorting: false,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', displayDate(order.stateUpdatedAt));
    },
  },
  {
    accessorKey: 'state',
    header: () => 'Status',
    enableSorting: false,
    cell: ({ getValue }) => {
      const status = getValue() as string;
      const variant = getOrderStatusVariant(status);
      return h(Badge, { class: 'text-xs', variant }, () => status);
    },
  },
  {
    accessorKey: 'supplier',
    header: 'User',
    enableSorting: false,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', order?.supplier?.name ?? 'N/A');
    },
  },
  {
    accessorKey: 'planValue',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Plan Value', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    enableSorting: true,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      const plans: ModelPaymentPlanResponse[] = order.plans ? order.plans : [];
      return h('div', { class: 'text-right' }, currency(calcPlanValue(plans)));
    },
  },
  {
    accessorKey: 'totalPaid',
    header: () => h('div', { class: 'text-right' }, 'Total Received'),
    enableSorting: false,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      return h('div', { class: 'text-right' }, currency(getTotalPaid(order)));
    },
  },
  {
    accessorKey: 'outstanding',
    header: ({ column }) => {
      return h(
        Button,
        {
          class: '!p-0',
          variant: 'ghost',
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
        },
        () => ['Outstanding', h(ArrowUpDown, { class: 'ml-2 h-4 w-4' })],
      );
    },
    enableSorting: true,
    cell: ({ row }) => {
      const order: ModelOrderResponse = row.original;
      const value = getOutstanding(order);
      return h('div', { class: 'text-right' }, currency(value));
    },
  },
];
